// Session observability only (error/performance monitoring + session
// replay) - not feature flags. Client-only by filename convention
// (though ssr is off site-wide anyway) since this touches window/localStorage.
// Session replay does send recordings of what a visitor does in the UI to
// LaunchDarkly's servers - a different thing from the "your project files
// never leave your machine" promise made elsewhere on this site (those
// still never go over the network; this is just interaction/error
// telemetry about the app itself), but worth knowing. privacySetting:
// 'none' - no PII-shaped inputs anywhere on this site (program/sample
// names, search boxes), so there's nothing worth redacting, and 'strict'
// was randomizing/garbling all on-screen text in replays, not just inputs.
export default defineNuxtPlugin(() => {
  const clientId = useRuntimeConfig().public.launchDarklyClientId
  if (!clientId) return

  // Telemetry is useful on every session, but its SDKs do not need to block the
  // first render of the tool. Load them once the browser is idle instead.
  const start = async () => {
    const [{ createClient }, { default: Observability }, { default: SessionReplay }] = await Promise.all([
      import('@launchdarkly/js-client-sdk'),
      import('@launchdarkly/observability'),
      import('@launchdarkly/session-replay'),
    ])

    // Tags every session with where it's coming from, so local dev/testing
    // traffic can be filtered out of (or isolated to) the LaunchDarkly
    // dashboard instead of mixing in with real visitor sessions.
    const isLocal = import.meta.dev || ['localhost', '127.0.0.1'].includes(window.location.hostname)

    createClient(
      clientId,
      { kind: 'user', anonymous: true, environment: isLocal ? 'local' : 'production' },
      {
        plugins: [
          new Observability({ tracingOrigins: true, networkRecording: { enabled: true, recordHeadersAndBody: false } }),
          new SessionReplay({ privacySetting: 'none' }),
        ],
      }
    )
  }

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => void start(), { timeout: 2500 })
  } else {
    window.setTimeout(() => void start(), 1500)
  }
})
